import { useRef, useCallback, useEffect } from 'react';

/**
 * A hook intended to capture outside interactions (click, touch, etc. events)
 * of a given element for use in "popup" components so they can be closed when
 * a user clicks outside of them.
 *
 * Usage:
 * ```
 * const MyComponent = props => {
 *   const ref = React.useRef(null);
 *
 *   const handleOutsideInteraction = event => {
 *     console.log("You clicked outside the Box!");
 *   };
 *
 *   useOutsideInteraction(
 *     refs: [ref],
 *     onOutsideInteraction: handleOutsideInteraction
 *   );
 *
 *   return <Box ref={ref}>{props.children}</Box>;
 * }
 * ```
 */

const useOutsideInteraction = props => {
  const {
    refs,
    onOutsideInteraction,
    disabled = false
  } = props;
  const isPointerDown = useRef(false);
  const onPointerDown = useCallback(event => {
    if (isValidOutsideInteraction(event, refs)) {
      isPointerDown.current = true;
    }
  }, [refs]);
  const onPointerUp = useCallback(event => {
    if (isPointerDown.current && isValidOutsideInteraction(event, refs)) {
      onOutsideInteraction(event);
    }
    isPointerDown.current = false;
  }, [refs, onOutsideInteraction]);
  useEffect(() => {
    if (disabled) return;
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
    };
  }, [refs, onPointerDown, onPointerUp, disabled]);
};
function isValidOutsideInteraction(event, refs) {
  /**
   * Only interested in the "main" mouse button
   * @see https://developer.mozilla.org/en-US/docs/Web/API/MouseEvent/button
   */
  if (event.button > 0) {
    return false;
  }
  return !refs.some(ref => ref.current?.contains(event.target));
}

export { useOutsideInteraction };
